import { useRef, useCallback } from 'react';
import { useRouter } from 'next/router';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { StickyNavApiEntry } from '@interfaces/models/sticky-nav';

interface UseStickyNavNavigationParams {
  setCurrentUrl: (url: string) => void;
  currentUniverse: number;
}

const useStickyNavNavigation = ({ setCurrentUrl, currentUniverse }: UseStickyNavNavigationParams) => {
  const router = useRouter();
  const { sendAnalyticEvent } = useAnalyticEvents('navigation');
  const isNavigatingRef = useRef<boolean>(false);

  const buildLinkHref = useCallback((entryPoint: StickyNavApiEntry): string => {
    return entryPoint.destination?.universalLink || '#';
  }, []);

  const handleEntryPointClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, entryPoint: StickyNavApiEntry) => {
      e.preventDefault();

      if (isNavigatingRef.current) {
        return;
      }

      const currentUrl = new URL(window.location.href);
      const baseHref = buildLinkHref(entryPoint);
      let targetUrl: URL;
      try {
        targetUrl = new URL(baseHref, window.location.origin);
      } catch {
        targetUrl = new URL(window.location.origin + baseHref);
      }

      const currentStickyCategoryId = currentUrl.searchParams.get('stickyCategoryId');
      const targetStickyCategoryId = targetUrl.searchParams.get('stickyCategoryId');

      const sameCategory = currentStickyCategoryId === targetStickyCategoryId;
      const samePathname = currentUrl.pathname === targetUrl.pathname;
      if (sameCategory && samePathname) {
        return;
      }

      isNavigatingRef.current = true;

      const pathnameMatches = currentUrl.pathname.replace(/\/$/, '') === targetUrl.pathname.replace(/\/$/, '');

      if (!pathnameMatches) {
        const pushResult = router.push(baseHref);
        if (pushResult && typeof pushResult.finally === 'function') {
          pushResult.finally(() => {
            if (typeof window !== 'undefined' && window.requestAnimationFrame) {
              window.requestAnimationFrame(() => {
                window.dispatchEvent(new PopStateEvent('popstate'));
              });
            }
            isNavigatingRef.current = false;
          });
        } else {
          isNavigatingRef.current = false;
        }
      } else {
        window.history.pushState(null, '', baseHref);
        setCurrentUrl(window.location.href);
        if (window.requestAnimationFrame) {
          window.requestAnimationFrame(() => {
            window.dispatchEvent(new PopStateEvent('popstate'));
            isNavigatingRef.current = false;
          });
        } else {
          isNavigatingRef.current = false;
        }
      }

      const isUniverseButton =
        (!entryPoint.destination?.categoryId || entryPoint.destination.categoryId === '') &&
        (entryPoint.mnemonic === 'men' || entryPoint.mnemonic === 'women');

      sendAnalyticEvent('click_sticky_navigation', {
        label: 'sticky_navigation',
        property: isUniverseButton ? '' : entryPoint.destination?.categoryId || '',
        value: currentUniverse,
      });
    },
    [router, currentUniverse, buildLinkHref, setCurrentUrl, sendAnalyticEvent],
  );

  return {
    handleEntryPointClick,
    buildLinkHref,
  };
};

export default useStickyNavNavigation;
